import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import {
  HdLocksKeysPeriodId,
  getHdLocksKeysGateRole,
  hdLocksKeysPeriodForDate,
} from '../../mandala/locks-keys/hd-locks-keys-metadata';
import { HdLocksKeysStateService } from '../../mandala/locks-keys/hd-locks-keys-state.service';

interface HdGateLocksKeysIconVm {
  readonly kind: 'lock' | 'key';
  readonly active: boolean;
  readonly tooltip: string;
  readonly periodId: HdLocksKeysPeriodId | null;
  readonly clickable: boolean;
}

/**
 * Lock-/key-icoon van een poort (Locks & Keys, spec 2026-10-10 §4); niets bij
 * een poort zonder rol. Een lock is altijd een lock; een key hangt af van de
 * periode van vandaag (vóór of vanaf de shift van 15 februari 2027): key in de
 * huidige periode = actief icoon, key in de andere periode = grijs icoon met
 * "was/zal zijn"-tooltip. Klikbaar enkel wanneer de chart-pagina een launcher
 * registreerde; de klik opent de Locks & Keys-modus (bij een key in de periode
 * waarin hij key is).
 *
 * Gedeeld door de kop van de poort-info en de activatie-kaartjes van het
 * Basis-paneel. Maat via `--hd-gate-locks-keys-icon-size` en de knop-padding
 * via `--hd-gate-locks-keys-icon-padding` / `--hd-gate-locks-keys-icon-touch-padding`.
 */
@Component({
  selector: 'hd-gate-locks-keys-icon',
  templateUrl: './hd-gate-locks-keys-icon.component.html',
  styleUrl: './hd-gate-locks-keys-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGateLocksKeysIconComponent {
  readonly gate = input.required<number>();
  /** Uit = nooit een knop, ook met launcher (bv. de gaststand). */
  readonly clickable = input(true, { transform: booleanAttribute });

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly locksKeysState = inject(HdLocksKeysStateService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly icon = computed<HdGateLocksKeysIconVm | null>(() => {
    const gate = this.gate();
    const role = getHdLocksKeysGateRole(gate);
    if (role == null) {
      return null;
    }
    const lang = this.lang();
    const t = (key: string, params?: Record<string, unknown>) =>
      this.gateInfoService.locksKeysTextIn(key, lang, params);
    const clickable = this.clickable() && this.locksKeysState.modeLauncher() != null;
    const withHint = (text: string) => (clickable ? `${text}\n${t('gateInfo.clickHint')}` : text);
    if (role.kind === 'lock') {
      return {
        kind: 'lock',
        active: true,
        tooltip: withHint(t('gateInfo.lockTooltip', { gate, role: t(`locks.${gate}.role`) })),
        periodId: null,
        clickable,
      };
    }
    const currentPeriod = hdLocksKeysPeriodForDate();
    const active = role.periodId === currentPeriod;
    const isFuture = role.periodId === 'sleepingPhoenix';
    const tooltipKey = active ? 'gateInfo.keyCurrentTooltip'
      : isFuture ? 'gateInfo.keyFutureTooltip'
        : 'gateInfo.keyPastTooltip';
    return {
      kind: 'key',
      active,
      tooltip: withHint(t(tooltipKey, {
        gate,
        lock: role.lock.gate,
        period: t(`periods.${role.periodId}.label`),
      })),
      periodId: role.periodId,
      clickable,
    };
  });

  protected open(): void {
    this.locksKeysState.modeLauncher()?.(this.icon()?.periodId ?? undefined);
  }
}
